import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Phone, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { ApexLogo } from "@/components/ApexLogo";
import { NAV_LINKS } from "@/lib/constants";

export default function Header({ settings }) {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur border-slate-200 shadow-sm" : "bg-white border-transparent"
      }`}
    >
      <div className="container-x flex h-[72px] items-center justify-between">
        <Link to="/" data-testid="header-logo-link" aria-label="ApexPath home">
          <ApexLogo />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `link-underline text-sm font-medium ${isActive ? "text-gold" : "text-navy-light hover:text-navy"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/book-consultation"
            data-testid="nav-book-consultation-btn"
            className="btn-gold hidden text-sm sm:inline-flex"
          >
            Book a Consultation <ArrowRight size={16} />
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                className="lg:hidden rounded-md p-2 text-navy"
                aria-label="Open menu"
              >
                <Menu size={26} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-navy text-white border-navy-light">
              <div className="mb-8 mt-2">
                <ApexLogo light />
              </div>
              <nav className="flex flex-col gap-1" aria-label="Mobile">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                      className={({ isActive }) =>
                        `rounded-md px-3 py-3 text-base font-medium transition-colors ${
                          isActive ? "bg-navy-light text-gold" : "text-slate-100 hover:bg-navy-light hover:text-white"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </nav>
              <SheetClose asChild>
                <button
                  onClick={() => navigate("/book-consultation")}
                  data-testid="mobile-book-consultation-btn"
                  className="btn-gold mt-6 w-full"
                >
                  Book a Consultation
                </button>
              </SheetClose>
              {settings?.phone && (
                <a
                  href={`tel:${settings.phone}`}
                  className="mt-4 flex items-center gap-2 px-3 text-sm text-slate-200"
                >
                  <Phone size={15} /> {settings.phone}
                </a>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
